// EN-5235: Enhanced ID checks for contactless check-in and pre-arrival, the same
// checks the kiosk runs (kiosk src/config/enhancedIdValidation.js).
//
// The checks run on the backend against the stored validateId scan. The visitor
// never waits for them and never sees the result: check-in goes by validateId's
// own answer, and the backend keeps the outcome in the admin's check history.
import type { OrgConfig } from '../types/orgConfig';
import type { EnhancedIdCheckPayload, EnhancedIdCheckResponse } from '../types/idValidation';

export const ENHANCED_ID_VALIDATION_ENTITLEMENT = 'ENHANCED_ID_VALIDATION';

// The backend answers in under 15s; a hung call is let go a little after that.
export const ENHANCED_ID_CHECKS_TIMEOUT_MS = 20000;

// On when the org has the entitlement and the site has at least one check on.
// Liveness is left out: there is no provider yet and the backend always answers
// NOT_APPLICABLE for it.
export const isEnhancedIdValidationOn = (
  config: Pick<OrgConfig, 'orgProducts' | 'enhancedIdValidation'> | null | undefined,
): boolean => {
  const entitled = !!config?.orgProducts?.products?.[0]?.entitlements?.some(
    (e) => e.key === ENHANCED_ID_VALIDATION_ENTITLEMENT && e.value === 'true',
  );
  const site = config?.enhancedIdValidation;
  return entitled && !!(site?.authenticityMatch || site?.fullNameMatch || site?.photoMatch);
};

// Leaves out empty values so the backend sees only what the visitor gave.
export const buildEnhancedIdCheckBody = ({
  attemptNumber,
  visitorFullName,
  visitPhotoImage,
}: {
  attemptNumber?: number | null;
  visitorFullName?: unknown;
  visitPhotoImage?: unknown;
}): EnhancedIdCheckPayload => {
  const body: EnhancedIdCheckPayload = {};
  if (typeof attemptNumber === 'number' && attemptNumber >= 1) {
    body.attemptNumber = attemptNumber;
  }
  if (typeof visitorFullName === 'string' && visitorFullName.trim()) {
    body.visitorFullName = visitorFullName.trim();
  }
  if (typeof visitPhotoImage === 'string' && visitPhotoImage) {
    body.visitPhotoImage = visitPhotoImage;
  }
  return body;
};

// One line for the logs: each check's type, status and reason, or why nothing ran.
export const describeEnhancedIdChecks = (response: EnhancedIdCheckResponse | null | undefined): string => {
  if (!response) return 'no response';
  if (response.notRunReason) return `not run (${response.notRunReason})`;
  if (!response.checks?.length) return 'no checks ran';
  return response.checks
    .map((c) => `${c.checkType}=${c.status}${c.reasonCode ? `(${c.reasonCode})` : ''}`)
    .join(', ');
};
